import { LocalDate, Timestamp, TuettuKieli } from '@lemon/core/model/common'
import {
  EI_LASKUNUMEROA_NUMERO,
  InvoiceSource,
  Lasku,
  Laskuasetukset,
  LaskuBase,
  LaskunAlv,
  LaskunOtsikonAvain,
  LaskunPerintatietojenLahetyskerta,
  LaskunTila,
  LaskunTuote,
  LaskunTyypit,
  LaskunumeroTyyppi,
  LaskuReskontra,
  TuoteAvaimet,
} from '../../model/lasku'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'
import { CountryLocalizationService, TranslationService } from '../translation.service'

import { Big } from 'big.js'

import { ValuuttakonversionTiedotKirjauksessa } from '@lemon/core-firebase/model/tiliote'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { StringService } from '@lemon/core/service/string.service'
import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'
import { LaskunQrKoodinCharacterSet, LaskunQrKoodinOsat, ViitenumeroService, VirtuaaliviivakoodinOsat } from '@lemon/core/service/viitenumero.service'
import { FirestoreBatchOrTransaction, FirestoreDoc, Precondition } from './lasku-tallennus.service'

// Checks if T is a plain object (not a class instance or built-in object)
type IsPlainObject<T> = T extends Function ? false : T extends Date | RegExp | Map<any, any> | Set<any> | Array<any> ? false : T extends object ? true : false

// Gets only non-function keys
type NonFunctionKeys<T> = {
  [K in keyof T]: T[K] extends Function ? never : K
}[keyof T] &
  string

type DotPaths<T> =
  IsPlainObject<T> extends true
    ? {
        [K in NonFunctionKeys<T>]: IsPlainObject<T[K]> extends true ? `${K}` | `${K}.${DotPaths<T[K]>}` : `${K}`
      }[NonFunctionKeys<T>]
    : never

type PathValue<T, P extends string> = P extends `${infer K}.${infer Rest}` ? (K extends keyof T ? PathValue<T[K], Rest> : never) : P extends keyof T ? T[P] : never

export interface LaskuChangeCollectorInterface {
  setJuurilaskuAsTheChange(juurilasku: Lasku): void
  setKasiteltavaAsTheChange(kasiteltava: LaskuBase): void
  deleteKasiteltava(kasiteltava: Pick<LaskuBase, 'avain'>, deleteValue: any): void
  addChange<K extends keyof LaskuBase>(juurilasku: Lasku, kasiteltava: LaskuBase, key: K, value: LaskuBase[K]): void
  addChangePath<P extends DotPaths<LaskuBase>>(juurilasku: Lasku, kasiteltava: LaskuBase, path: P, value: PathValue<LaskuBase, P>): void
  addPerintatiedotLahetysChange<K extends keyof LaskunPerintatietojenLahetyskerta>(lahetysavain: string, key: K, value: LaskunPerintatietojenLahetyskerta[K])
  addJuurilaskuChange<K extends keyof Lasku>(path: K, value: Lasku[K]): void
  addJuurilaskuChangePath<P extends DotPaths<Lasku>>(path: P, value: PathValue<Lasku, P>): void
  addHakuChange(value: object): void
  transferChangesToBatch(doc: FirestoreDoc, batch: FirestoreBatchOrTransaction)
}

export class LaskuChangeNoopCollector implements LaskuChangeCollectorInterface {
  setJuurilaskuAsTheChange(juurilasku: Lasku): void {}
  setKasiteltavaAsTheChange(kasiteltava: LaskuBase): void {}
  deleteKasiteltava(kasiteltava: Pick<LaskuBase, 'avain'>, deleteValue: any): void {}
  addChange<K extends keyof LaskuBase>(juurilasku: Lasku, kasiteltava: LaskuBase, key: K, value: LaskuBase[K]): void {}
  addChangePath<P extends DotPaths<LaskuBase>>(juurilasku: Lasku, kasiteltava: LaskuBase, path: P, value: PathValue<LaskuBase, P>): void {}
  addPerintatiedotLahetysChange<K extends keyof LaskunPerintatietojenLahetyskerta>(lahetysavain: string, key: K, value: LaskunPerintatietojenLahetyskerta[K]) {}
  addJuurilaskuChange<K extends keyof Lasku>(path: K, value: Lasku[K]): void {}
  addJuurilaskuChangePath<P extends DotPaths<Lasku>>(path: P, value: PathValue<Lasku, P>): void {}
  addHakuChange(value: object): void {}
  transferChangesToBatch(doc: FirestoreDoc, batch: FirestoreBatchOrTransaction) {}
}

export class LaskuChangeCollector implements LaskuChangeCollectorInterface {
  constructor(private docType: 'new-document' | 'existing-document') {}
  private _changeAsObject = {}
  setJuurilaskuAsTheChange(juurilasku: Lasku): void {
    this._changeAsObject = juurilasku
  }
  setKasiteltavaAsTheChange(kasiteltava: LaskuBase): void {
    this._setState('liittyvatLaskut.' + kasiteltava.avain, kasiteltava)
  }
  deleteKasiteltava(kasiteltava: Pick<LaskuBase, 'avain'>, deleteValue: any): void {
    this._setState('liittyvatLaskut.' + kasiteltava.avain, deleteValue)
  }
  addChange<K extends keyof LaskuBase>(juurilasku: Lasku, kasiteltava: LaskuBase, key: K, value: LaskuBase[K]): void {
    // Muutos koskee juurilaskua
    if (juurilasku.avain === kasiteltava.avain) {
      this._setState(key, value)
    } else {
      this._setState('liittyvatLaskut.' + kasiteltava.avain + '.' + key, value)
    }
  }
  addChangePath<P extends DotPaths<LaskuBase>>(juurilasku: Lasku, kasiteltava: LaskuBase, path: P, value: PathValue<LaskuBase, P>): void {
    // Muutos koskee juurilaskua
    if (juurilasku.avain === kasiteltava.avain) {
      this._setState(path, value)
    } else {
      this._setState('liittyvatLaskut.' + kasiteltava.avain + '.' + path, value)
    }
  }
  addPerintatiedotLahetysChange<K extends keyof LaskunPerintatietojenLahetyskerta>(lahetysavain: string, key: K, value: LaskunPerintatietojenLahetyskerta[K]) {
    this._setState('perintatiedot.lahetykset.' + lahetysavain + '.' + key, value)
  }
  addJuurilaskuChange<K extends keyof Lasku>(path: K, value: Lasku[K]): void {
    this._setState(path, value)
  }
  addJuurilaskuChangePath<P extends DotPaths<Lasku>>(path: P, value: PathValue<Lasku, P>): void {
    this._setState(path, value)
  }
  addHakuChange(value: object): void {
    this._changeAsObject['haku'] = value
  }
  transferChangesToBatchWithPrecondition(doc: FirestoreDoc, batch: FirestoreBatchOrTransaction, precondition: Precondition) {
    if (this.docType === 'existing-document') {
      batch.update(doc, this._changeAsObject, precondition)
    } else {
      throw new Error('Cannot transfer changes with precondition for new document')
    }
  }
  transferChangesToBatch(doc: FirestoreDoc, batch: FirestoreBatchOrTransaction) {
    if (this.docType === 'existing-document') {
      const haku = this._changeAsObject['haku']
      delete this._changeAsObject['haku']
      batch.set(doc, this._changeAsObject, { merge: true })
      if (haku) {
        batch.update(doc, { haku: haku })
      }
    } else {
      batch.set(doc, this._changeAsObject, { merge: true })
    }
  }
  private _setState(path: string, value: any): void {
    let curr = this._changeAsObject
    const pathParts = path.split('.')
    const lastPathPart = pathParts.pop()
    for (const part of pathParts) {
      if (!curr[part]) {
        curr[part] = {}
      }
      curr = curr[part]
    }
    curr[lastPathPart] = value
  }
}

// TODO! TÄTÄ EI KÄYTETÄ MISSÄÄN??
// export enum PdfTemplate {
//   PERUS = 'perinteinen'
// }

export interface LaskuryppaanSummat {
  laskunSummat: LaskunSummat
  /**
   * Viimeisin tavallinen lasku + huomautuksien kulut ja korot - hyvitykset
   */
  rypasYhteensa: Big
  rypasYhteensaAlv0: Big
  huomautuskorot: Big
  huomautuskulut: Big
  hyvitykset: Big
  hyvityksetAlv0: Big
}

export interface LaskuryppaanSummatJaTila extends LaskuryppaanSummat {
  avoinna: Big
  reskontrasumma: Big
  tila: LaskunTila
}

export interface LaskunSummat {
  yhteensaIlmanAlv: Big
  yhteensaAlv: Big
  yhteensaKaikki: Big
  alennustaYhteensaIlmanAlv: Big
  alennustaYhteensaAlv: Big
  alvErittelyMap: { [key: string]: AlvErittelynOsa }
  alvErittely: AlvErittelynOsa[]
}

export interface AlvErittelynOsa {
  alennustaIlmanAlv: Big
  alennustaAlv: Big
  alvitonSumma: Big
  alvinSumma: Big
  alvKanta: LaskunAlv
  projekti?: string
}

export class ReskontraService {
  constructor(private dateService: DateService) {}

  annaReskontranSummaPaivalle(reskontra: LaskuReskontra[], pvm: Date): Big {
    let maara = new Big('0')
    if (reskontra) {
      for (const reskontraEntry of reskontra) {
        const paiviaValissa = this.dateService.paiviaValissa(pvm, reskontraEntry.pvm.toDate())
        if (paiviaValissa >= 0) {
          maara = maara.plus(reskontraEntry.suoritus)
        }
      }
    }
    return maara.round(2, Big.roundHalfUp)
  }

  annaReskontranSummaHetkelle(reskontra: LaskuReskontra[], pvm: Timestamp): Big {
    let maara = new Big('0')
    if (reskontra) {
      for (const reskontraEntry of reskontra) {
        if (reskontraEntry.pvm.toMillis() <= pvm.toMillis()) {
          maara = maara.plus(reskontraEntry.suoritus)
        }
      }
    }
    return maara.round(2, Big.roundHalfUp)
  }

  annaReskontranSumma(reskontra: LaskuReskontra[]): Big {
    let maara = new Big('0')
    if (reskontra) {
      for (const reskontraEntry of reskontra) {
        maara = maara.plus(reskontraEntry.suoritus)
      }
    }
    return maara.round(2, Big.roundHalfUp)
  }
}

export class LaskuSharedService {
  constructor(
    private dateService: DateService,
    private translationService: TranslationService,
    private countryLocalizationService: CountryLocalizationService,
    private currencyService: CurrencyService,
    private reskontraService: ReskontraService,
    private timestampProvider: TimestampProviderBase,
    private viitenumeroService: ViitenumeroService,
    private _codeCheckService: CodeCheckService,
    private _stringService: StringService,
  ) {}

  valmisteleTeksti(valmisteltava: string): string {
    if (!valmisteltava) {
      return ''
    }
    return valmisteltava.replace(/[\n\r]/g, '<br />')
  }

  annaLaskunumeroViitenumerosta(viite: string): number | null {
    const viiteIlmanValilyonteja = this._stringService.removeAllWhiteSpaces(viite)
    if (!viiteIlmanValilyonteja || viiteIlmanValilyonteja.trim() === '') {
      return null
    }
    const ilmanRfTarkistetta = viiteIlmanValilyonteja.toUpperCase().startsWith('RF') ? viiteIlmanValilyonteja.substring(4) : viiteIlmanValilyonteja
    const laskunumero = ilmanRfTarkistetta.substring(0, ilmanRfTarkistetta.length - 1)
    if (this.currencyService.onkoMerkkijonoKokonaisluku(laskunumero)) {
      return Number(laskunumero)
    }
    return null
  }

  annaMuotoiltuLaskunumero(juurilasku: Lasku, kasiteltava: LaskuBase) {
    if (this.onkoUlkoinenLasku(kasiteltava)) {
      return kasiteltava.ulkoinenLaskunro
    }

    if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
      return this.annaLaskunumero(kasiteltava)
    }
    return this.annaLaskunumero(juurilasku)
  }

  annaLaskunumero(lasku: LaskuBase): string {
    if (this.onkoUlkoinenLasku(lasku)) {
      return lasku.ulkoinenLaskunro
    }
    return this.annaLaskunumeroNumerollaJaTyypilla(lasku.nro, lasku.nrotyyppi)
  }

  annaLaskunumeroNumerollaJaTyypilla(numero: number, tyyppi: LaskunumeroTyyppi): string {
    if (numero && numero !== EI_LASKUNUMEROA_NUMERO) {
      if (tyyppi === LaskunumeroTyyppi.KORJAUS) {
        return 'K' + numero
      } else if (tyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        return 'M' + numero
      }
      return numero + ''
    }
    return ''
  }

  formatoiPvm(paikallinen: LocalDate, date: Timestamp, kasiteltava: LaskuBase): string {
    const d = this.dateService.annaPaikallinenPvm(date, paikallinen)
    if (d && kasiteltava && kasiteltava.kieli) {
      return this.dateService.muotoilePaikallinenPaiva(d, kasiteltava.kieli)
    }
    return ''
  }

  laskeMaksuaikaa(kasiteltava: LaskuBase): number {
    if (kasiteltava) {
      const e = this.dateService.annaPaikallinenPvm(kasiteltava.erapvm, kasiteltava.erapvml)
      const p = this.dateService.annaPaikallinenPvm(kasiteltava.pvm, kasiteltava.pvml)
      if (e && p) {
        return this.dateService.paiviaValissaPaikallinen(e, p)
      }
    }
    return 0
  }

  formatoiBigRaha(amount: Big, lasku: LaskuBase): string {
    if (lasku) {
      return this.currencyService.formatoiBigRaha(amount, lasku.valuutta, lasku.kieli)
    }
    return ''
  }

  formatoiRaha(amount: number, lasku: LaskuBase): string {
    if (lasku) {
      return this.currencyService.formatoiRaha(amount, lasku.valuutta, lasku.kieli)
    }
    return ''
  }

  annaAlvNimi(lasku: LaskuBase, alv: LaskunAlv): string {
    if (alv && alv.prosentti > -0.01) {
      return this.currencyService.formatoiDesimaali(alv.prosentti, 2, lasku.kieli) + '%'
    }
    return 'Ei ALV:TÄ!'
  }

  annaLokalisoituMerkkijono(avain: string, kasiteltava: LaskuBase, parametrit?: any): string {
    if (!avain || !kasiteltava || !kasiteltava.kieli) {
      return ''
    }
    // console.log(avain)
    return this.translationService.lokalisoi(avain, kasiteltava.kieli, parametrit)
  }

  annaMuotoiltuViitenumero(juurilasku: Lasku): string {
    if (juurilasku) {
      if (juurilasku.viitenumero && juurilasku.viitenumero.length > 2) {
        return this.viitenumeroService.muotoileViitenumero(juurilasku.viitenumero)
      }
      const viitenumero = this.annaViitenumeroLaskunumerosta(juurilasku)
      if (viitenumero) {
        return this.viitenumeroService.muotoileViitenumero(viitenumero)
      }
    }
    return ''
  }

  annaViitenumeroLaskunumerosta(juurilasku: Lasku): string | null {
    if (!juurilasku || this.onkoUlkoinenLasku(juurilasku) || !juurilasku.nro || juurilasku.nro === EI_LASKUNUMEROA_NUMERO || !juurilasku.p) {
      return null
    }
    const viitenumero = this.viitenumeroService.luoViitenumero(juurilasku.nro.toString())
    if (this.dateService.compareNumberDates(juurilasku.p, '>', 250829)) {
      return this.viitenumeroService.luoRfViitenumero(viitenumero)
    }
    return viitenumero
  }

  private annaLaskunSummatJaTilaPaivamaaralle(
    juurilasku: Lasku,
    pvm: Date,
    viimeinenTavallinen: LaskuBase,
    muistutuksetJaHyvitykset: LaskuBase[],
    reskontra: LaskuReskontra[],
    laskeUudelleen: boolean = true,
  ): LaskuryppaanSummatJaTila {
    const reskontraSumma = this.reskontraService.annaReskontranSummaPaivalle(reskontra, pvm)
    return this.annaLaskunSummatJaTilaReskontrasummallaPaivamaaralle(juurilasku, reskontraSumma, viimeinenTavallinen, muistutuksetJaHyvitykset, laskeUudelleen, pvm)
  }

  private annaLaskunSummatJaTila(
    juurilasku: Lasku,
    viimeinenTavallinen: LaskuBase,
    muistutuksetJaHyvitykset: LaskuBase[],
    reskontra: LaskuReskontra[],
    laskeUudelleen: boolean = true,
  ): LaskuryppaanSummatJaTila {
    return this.annaLaskunSummatJaTilaReskontralle(juurilasku, viimeinenTavallinen, muistutuksetJaHyvitykset, reskontra, laskeUudelleen)
  }

  public annaLaskunSummatJaTilaReskontralle(
    juurilasku: Lasku,
    viimeinenTavallinen: LaskuBase,
    muistutuksetJaHyvitykset: LaskuBase[],
    reskontra: LaskuReskontra[],
    laskeUudelleen: boolean = true,
  ): LaskuryppaanSummatJaTila {
    const reskontraSumma = this.reskontraService.annaReskontranSumma(reskontra)
    return this.annaLaskunSummatJaTilaReskontrasummalla(juurilasku, reskontraSumma, viimeinenTavallinen, muistutuksetJaHyvitykset, laskeUudelleen)
  }

  private annaLaskuryppaanSummat(viimeinenTavallinen: LaskuBase, muistutuksetJaHyvitykset: LaskuBase[], laskeUudelleen: boolean): LaskuryppaanSummat {
    const summat = this.annaLaskunSummat(viimeinenTavallinen)

    let ryppaanSumma = laskeUudelleen ? summat.yhteensaKaikki : new Big(viimeinenTavallinen.summa)
    let ryppaanSummaAlv0 = summat.yhteensaIlmanAlv
    let huomautuskorot = new Big('0')
    let huomautuskulut = new Big('0')
    let hyvitykset = new Big('0')
    let hyvityksetAlv0 = new Big('0')

    for (const korvaustieto of muistutuksetJaHyvitykset) {
      if (korvaustieto.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        const kulut = this.annaMuistutuslaskunKulut(korvaustieto)
        ryppaanSumma = ryppaanSumma.plus(kulut.korot)
        ryppaanSumma = ryppaanSumma.plus(kulut.kulut)
        ryppaanSummaAlv0 = ryppaanSumma.plus(kulut.korot)
        ryppaanSummaAlv0 = ryppaanSumma.plus(kulut.kulut)
        huomautuskorot = huomautuskorot.plus(kulut.korot)
        huomautuskulut = huomautuskulut.plus(kulut.kulut)
      } else if (korvaustieto.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
        const hyvityksenSummat = this.annaLaskunSummat(korvaustieto)
        ryppaanSumma = ryppaanSumma.plus(hyvityksenSummat.yhteensaKaikki)
        ryppaanSummaAlv0 = ryppaanSummaAlv0.plus(hyvityksenSummat.yhteensaIlmanAlv)
        hyvitykset = hyvitykset.plus(hyvityksenSummat.yhteensaKaikki)
        hyvityksetAlv0 = hyvityksetAlv0.plus(hyvityksenSummat.yhteensaIlmanAlv)
      }
    }

    return {
      rypasYhteensa: ryppaanSumma,
      rypasYhteensaAlv0: ryppaanSummaAlv0,
      laskunSummat: summat,
      huomautuskorot: huomautuskorot,
      huomautuskulut: huomautuskulut,
      hyvitykset: hyvitykset,
      hyvityksetAlv0: hyvityksetAlv0,
    }
  }

  annaLaskuryppaanSummatLaskulle(juurilasku: Lasku, laskeUudelleen: boolean = true): LaskuryppaanSummat {
    const viimeinenTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
    const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
    return this.annaLaskuryppaanSummat(viimeinenTavallinen, muistutuksetJaHyvitykset, laskeUudelleen)
  }

  private annaLaskunSummatJaTilaReskontrasummallaPaivamaaralle(
    juurilasku: Lasku,
    reskontraSumma: Big,
    viimeinenTavallinen: LaskuBase,
    muistutuksetJaHyvitykset: LaskuBase[],
    laskeUudelleen: boolean = true,
    pvm: Date = new Date(),
  ): LaskuryppaanSummatJaTila {
    const laskuryppaanSummat = this.annaLaskuryppaanSummat(viimeinenTavallinen, muistutuksetJaHyvitykset, laskeUudelleen)
    const avoinna = laskuryppaanSummat.rypasYhteensa.minus(reskontraSumma)

    let tila = LaskunTila.avoin
    if (this.onkoKasiteltavaMitatoity(juurilasku, viimeinenTavallinen)) {
      tila = LaskunTila.mitatoity
    } else if (this.onkoKasiteltavaLuonnos(juurilasku, viimeinenTavallinen)) {
      tila = LaskunTila.luonnos
    } else if (juurilasku.tila === LaskunTila.luottotappio) {
      tila = LaskunTila.luottotappio
    } else {
      tila = this.annaLaskunTilaReskontranMukaan(avoinna, reskontraSumma, viimeinenTavallinen, pvm)
    }

    return {
      avoinna: avoinna,
      rypasYhteensa: laskuryppaanSummat.rypasYhteensa,
      rypasYhteensaAlv0: laskuryppaanSummat.rypasYhteensaAlv0,
      laskunSummat: laskuryppaanSummat.laskunSummat,
      reskontrasumma: reskontraSumma,
      tila: tila,
      huomautuskorot: laskuryppaanSummat.huomautuskorot,
      huomautuskulut: laskuryppaanSummat.huomautuskulut,
      hyvitykset: laskuryppaanSummat.hyvitykset,
      hyvityksetAlv0: laskuryppaanSummat.hyvityksetAlv0,
    }
  }

  private annaLaskunSummatJaTilaReskontrasummalla(
    juurilasku: Lasku,
    reskontraSumma: Big,
    viimeinenTavallinen: LaskuBase,
    muistutuksetJaHyvitykset: LaskuBase[],
    laskeUudelleen: boolean = true,
  ): LaskuryppaanSummatJaTila {
    return this.annaLaskunSummatJaTilaReskontrasummallaPaivamaaralle(juurilasku, reskontraSumma, viimeinenTavallinen, muistutuksetJaHyvitykset, laskeUudelleen)
  }

  annaLaskunTilaReskontranMukaan(avoinna: Big, reskontraSumma: Big, viimeinenTavallinen: LaskuBase, pvm: Date = new Date()): LaskunTila {
    let tila = LaskunTila.avoin
    if (avoinna.round(2, Big.roundHalfUp).eq('0')) {
      if (reskontraSumma.gt(new Big('0'))) {
        tila = LaskunTila.maksettu
      } else {
        tila = LaskunTila.hyvitetty
      }
    } else if (avoinna.round(2, Big.roundHalfUp).lt('0')) {
      tila = reskontraSumma.lte(new Big('0')) ? LaskunTila.hyvitetty : LaskunTila.maksettuLiikaa
    } else if (this.onkoLaskunErapaivaMennytPaivalle(viimeinenTavallinen, pvm)) {
      tila = LaskunTila.eraantynyt
    }
    return tila
  }

  // annaLaskunHyvityssummaHetkelle(juurilasku: Lasku, pvm: Timestamp): Big {
  //   const hyvityslaskut = this.annaLaskuryppaanHyvityslaskutHetkelle(juurilasku, pvm)
  //   let hyvitykset = new Big('0')
  //   for (const hyvityslasku of hyvityslaskut) {
  //     const hyvityksenSummat = this.annaLaskunSummat(hyvityslasku)
  //     hyvitykset = hyvitykset.plus(hyvityksenSummat.yhteensaKaikki)
  //   }
  //   return hyvitykset
  // }

  // annaLaskunSummatLaskulle(juurilasku: Lasku): LaskuryppaanSummat {
  //   const viimeinenTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
  //   const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
  //   return this.annaLaskuryppaanSummat(viimeinenTavallinen, muistutuksetJaHyvitykset)
  // }

  annaLaskunSummatJaTilaLaskulle(juurilasku: Lasku, reskontra: LaskuReskontra[], laskeUudelleen: boolean = true): LaskuryppaanSummatJaTila {
    const viimeinenTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
    const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
    return this.annaLaskunSummatJaTila(juurilasku, viimeinenTavallinen, muistutuksetJaHyvitykset, reskontra, laskeUudelleen)
  }

  annaLaskunReskontraJaHyvityssummaHetkelle(juurilasku: Lasku, pvm: Timestamp, reskontra: LaskuReskontra[]): { reskontrasumma: Big; hyvityssumma: Big } {
    const reskontrasumma = this.reskontraService.annaReskontranSummaHetkelle(reskontra, pvm)
    const hyvityslaskut = this.annaLaskuryppaanHyvityslaskutHetkelle(juurilasku, pvm)
    let hyvitykset = new Big('0')
    for (const hyvityslasku of hyvityslaskut) {
      const hyvityksenSummat = this.annaLaskunSummat(hyvityslasku)
      hyvitykset = hyvitykset.plus(hyvityksenSummat.yhteensaKaikki)
    }
    return {
      hyvityssumma: hyvitykset,
      reskontrasumma: reskontrasumma,
    }
  }

  // OTETAANKO HUOMIOON KAIKKI MUIKKARIT?
  annaLaskunSummatJaTilaLaskulleTietyllaPaivamaaralla(juurilasku: Lasku, pvm: Date, reskontra: LaskuReskontra[]): LaskuryppaanSummatJaTila {
    const viimeinenTavallinen = this.annaViimeisinTavallinenLaskuPaivamaaralle(juurilasku, pvm)
    const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvityksetPaivamaaralle(juurilasku, pvm)
    return this.annaLaskunSummatJaTilaPaivamaaralle(juurilasku, pvm, viimeinenTavallinen, muistutuksetJaHyvitykset, reskontra, true)
  }

  // annaLaskunSummatJaTilaLaskulle(juurilasku: Lasku, laskeUudelleen: boolean = true): LaskuryppaanSummatJaTila {
  //   const viimeinenTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
  //   const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
  //   return this.annaLaskunSummatJaTila(juurilasku, viimeinenTavallinen, muistutuksetJaHyvitykset, juurilasku.reskontra, laskeUudelleen)
  // }

  annaLaskunSummatJaTilaLaskulleReskontralla(juurilasku: Lasku, reskontra: LaskuReskontra[], laskeUudelleen: boolean = true): LaskuryppaanSummatJaTila {
    const viimeinenTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
    const muistutuksetJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
    return this.annaLaskunSummatJaTilaReskontralle(juurilasku, viimeinenTavallinen, muistutuksetJaHyvitykset, reskontra, laskeUudelleen)
  }

  paivitaLaskunTila(juurilasku: Lasku, reskontra: LaskuReskontra[], collector: LaskuChangeCollectorInterface): void {
    juurilasku.tila = null
    juurilasku.tila = this.annaLaskunSummatJaTilaLaskulleReskontralla(juurilasku, reskontra).tila
    collector.addJuurilaskuChange('tila', juurilasku.tila)
  }

  annaMuistutuslaskunKulutTalleLaskulle(juurilasku: Lasku, kasiteltava: LaskuBase): { korot: Big; kulut: Big } {
    let edellinenMuikkari: LaskuBase = null
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    for (const korvaava of liittyvatLaskut) {
      if (korvaava.avain === kasiteltava.avain) {
        break
      }
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        edellinenMuikkari = korvaava
      } else if (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
        edellinenMuikkari = null
      }
    }

    const nykyisenKulut = this.annaMuistutuslaskunKulut(kasiteltava)
    if (edellinenMuikkari) {
      const edellisenKulut = this.annaMuistutuslaskunKulut(edellinenMuikkari)
      nykyisenKulut.kulut = nykyisenKulut.kulut.minus(edellisenKulut.kulut)
      nykyisenKulut.korot = nykyisenKulut.korot.minus(edellisenKulut.korot)
    }

    return nykyisenKulut
  }

  annaMuistutuslaskunKulut(kasiteltava: LaskuBase): { korot: Big; kulut: Big } {
    const paluuarvo = { korot: new Big('0'), kulut: new Big('0') }
    // return (tuote.tuote.avain !== TuoteAvaimet.HUOMAUTUSKULU && tuote.tuote.avain !== TuoteAvaimet.KORKOKULU) || Math.floor(tuote.hinta * 100) !== 0
    if (kasiteltava && kasiteltava.tuotteet) {
      for (const tuote of kasiteltava.tuotteet) {
        if (tuote && tuote.tuote) {
          if (tuote.tuote.avain === TuoteAvaimet.HUOMAUTUSKULU) {
            paluuarvo.kulut = paluuarvo.kulut.add(this.annaLaskurivinYhteensaLaskunTuotteelle(tuote))
          }
          if (tuote.tuote.avain === TuoteAvaimet.KORKOKULU) {
            paluuarvo.korot = paluuarvo.korot.add(this.annaLaskurivinYhteensaLaskunTuotteelle(tuote))
          }
        }
      }
    }
    return paluuarvo
  }

  private annaLaskuryppaanViimeisinMuistutusJaHyvityksetPaivamaaralle(juurilasku: Lasku, pvm: Date): LaskuBase[] {
    if (!this.annaLiittyvatLaskut(juurilasku).length) {
      return []
    }
    const kaikki = this.annaLaskuryppaanHyvityslaskutPaivamaaralle(juurilasku, pvm)
    const vikaMuikkari = this.annaLaskuryppaanViimeisinMuistutuslaskuPaivamaaralle(juurilasku, pvm)
    if (vikaMuikkari) {
      kaikki.push(vikaMuikkari)
    }
    return kaikki
  }

  annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku: Lasku): LaskuBase[] {
    if (!this.annaLiittyvatLaskut(juurilasku).length) {
      return []
    }
    const kaikki = this.annaLaskuryppaanHyvityslaskut(juurilasku)
    const vikaMuikkari = this.annaLaskuryppaanViimeisinMuistutuslasku(juurilasku)
    if (vikaMuikkari) {
      kaikki.push(vikaMuikkari)
    }
    return kaikki
  }

  annaLaskuryppaanMuistutuksetJaHyvitykset(juurilasku: Lasku): LaskuBase[] {
    if (!this.annaLiittyvatLaskut(juurilasku).length) {
      return []
    }
    const muistutukset = this.annaLaskuryppaanMuistutuslaskut(juurilasku)
    muistutukset.push(...this.annaLaskuryppaanHyvityslaskut(juurilasku))
    return muistutukset
  }

  private annaLaskuryppaanViimeisinMuistutuslaskuPaivamaaralle(juurilasku: Lasku, pvm: Date): LaskuBase | null {
    const muikkarit = this.annaLaskuryppaanMuistutuslaskut(juurilasku).filter(a => {
      return this.dateService.paiviaValissa(pvm, a.pvm.toDate()) > -1
    })
    return muikkarit.length > 0 ? muikkarit[muikkarit.length - 1] : null
  }

  annaLaskuryppaanViimeisinMuistutuslasku(juurilasku: Lasku): LaskuBase | null {
    const muikkarit = this.annaLaskuryppaanMuistutuslaskut(juurilasku)
    return muikkarit.length > 0 ? muikkarit[muikkarit.length - 1] : null
  }

  annaLaskuryppaanMuistutuslaskut(juurilasku: Lasku): LaskuBase[] {
    // HUOM! Jos ehtoja muutetaan, katso myös this.annaMuistutuslaskunNumero
    let muistutukset: LaskuBase[] = []
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        muistutukset.push(korvaava)
      } else if (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
        muistutukset = []
      }
    }
    return muistutukset
  }

  annaLaskuryppaanHyvityslaskutHetkelle(juurilasku: Lasku, pvm: Timestamp): LaskuBase[] {
    const hyvitykset: LaskuBase[] = []
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.HYVITYS && korvaava.pvm.toMillis() < pvm.toMillis()) {
        hyvitykset.push(korvaava)
      }
    }
    return hyvitykset
  }

  annaLaskuryppaanPaalaskunEriVersiot(juurilasku: Lasku): LaskuBase[] {
    const hyvitykset: LaskuBase[] = [juurilasku]
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
        hyvitykset.push(korvaava)
      }
    }
    return hyvitykset
  }

  annaLaskuryppaanViimeisinHyvityslasku(juurilasku: Lasku): LaskuBase | null {
    const hyvitykset = this.annaLaskuryppaanHyvityslaskut(juurilasku)
    return hyvitykset.length > 0 ? hyvitykset[hyvitykset.length - 1] : null
  }

  annaLaskuryppaanHyvityslaskut(juurilasku: Lasku): LaskuBase[] {
    const hyvitykset: LaskuBase[] = []
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
        hyvitykset.push(korvaava)
      }
    }
    return hyvitykset
  }

  private annaLaskuryppaanHyvityslaskutPaivamaaralle(juurilasku: Lasku, pvm: Date): LaskuBase[] {
    const hyvitykset: LaskuBase[] = []
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.HYVITYS && this.dateService.paiviaValissa(pvm, korvaava.pvm.toDate()) > -1) {
        hyvitykset.push(korvaava)
      }
    }
    return hyvitykset
  }

  paivitaRyppaanJaKasiteltavanSummat(juurilasku: Lasku, kasiteltava: LaskuBase, reskontra: LaskuReskontra[], collector: LaskuChangeCollectorInterface): void {
    const summat = this.annaLaskunSummat(kasiteltava)

    const reskontraJaHyvitykset = this.annaLaskunReskontraJaHyvityssummaHetkelle(juurilasku, kasiteltava.pvm, reskontra)
    const reskontranSumma = this.reskontraService.annaReskontranSumma(reskontra)

    kasiteltava.avoinna = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaKaikki.minus(reskontranSumma)) // .minus(hyvitystenSummat.yhteensaKaikki)
    kasiteltava.summa = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaKaikki)
    kasiteltava.summaAlv0 = this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaIlmanAlv)
    kasiteltava.summaHyvityksetLaskunPvm = this.currencyService.muutaBigDecimalRahaksi(reskontraJaHyvitykset.hyvityssumma)
    kasiteltava.summaReskontraLaskunPvm = this.currencyService.muutaBigDecimalRahaksi(reskontraJaHyvitykset.reskontrasumma)

    collector.addChange(juurilasku, kasiteltava, 'avoinna', kasiteltava.avoinna)
    collector.addChange(juurilasku, kasiteltava, 'summa', kasiteltava.summa)
    collector.addChange(juurilasku, kasiteltava, 'summaAlv0', kasiteltava.summaAlv0)
    collector.addChange(juurilasku, kasiteltava, 'summaHyvityksetLaskunPvm', kasiteltava.summaHyvityksetLaskunPvm)
    collector.addChange(juurilasku, kasiteltava, 'summaReskontraLaskunPvm', kasiteltava.summaReskontraLaskunPvm)

    if (kasiteltava.summa < 0) {
      kasiteltava.nrotyyppi = LaskunumeroTyyppi.HYVITYS
      collector.addChange(juurilasku, kasiteltava, 'nrotyyppi', kasiteltava.nrotyyppi)
    }

    const viimeisinTavallinen = this.annaViimeisinTavallinenLasku(juurilasku)
    if (viimeisinTavallinen.avain !== kasiteltava.avain) {
      const summatViimeisinTavallinen = this.annaLaskunSummat(viimeisinTavallinen)
      const reskontraJaHyvityksetViimeisinTavallinen = this.annaLaskunReskontraJaHyvityssummaHetkelle(juurilasku, viimeisinTavallinen.pvm, reskontra)

      viimeisinTavallinen.avoinna = this.currencyService.muutaBigDecimalRahaksi(summatViimeisinTavallinen.yhteensaKaikki.minus(reskontranSumma)) // .minus(hyvitystenSummat.yhteensaKaikki)
      viimeisinTavallinen.summa = this.currencyService.muutaBigDecimalRahaksi(summatViimeisinTavallinen.yhteensaKaikki)
      viimeisinTavallinen.summaAlv0 = this.currencyService.muutaBigDecimalRahaksi(summatViimeisinTavallinen.yhteensaIlmanAlv)
      viimeisinTavallinen.summaHyvityksetLaskunPvm = this.currencyService.muutaBigDecimalRahaksi(reskontraJaHyvityksetViimeisinTavallinen.hyvityssumma)
      viimeisinTavallinen.summaReskontraLaskunPvm = this.currencyService.muutaBigDecimalRahaksi(reskontraJaHyvityksetViimeisinTavallinen.reskontrasumma)

      collector.addChange(juurilasku, viimeisinTavallinen, 'avoinna', viimeisinTavallinen.avoinna)
      collector.addChange(juurilasku, viimeisinTavallinen, 'summa', viimeisinTavallinen.summa)
      collector.addChange(juurilasku, viimeisinTavallinen, 'summaAlv0', viimeisinTavallinen.summaAlv0)
      collector.addChange(juurilasku, viimeisinTavallinen, 'summaHyvityksetLaskunPvm', viimeisinTavallinen.summaHyvityksetLaskunPvm)
      collector.addChange(juurilasku, viimeisinTavallinen, 'summaReskontraLaskunPvm', viimeisinTavallinen.summaReskontraLaskunPvm)

      if (viimeisinTavallinen.summa < 0) {
        viimeisinTavallinen.nrotyyppi = LaskunumeroTyyppi.HYVITYS
        collector.addChange(juurilasku, viimeisinTavallinen, 'nrotyyppi', viimeisinTavallinen.nrotyyppi)
      }
    }

    const muistutusJaHyvitykset = this.annaLaskuryppaanViimeisinMuistutusJaHyvitykset(juurilasku)
    const laskuryppaanSummat = this.annaLaskuryppaanSummat(viimeisinTavallinen, muistutusJaHyvitykset, true)

    const summaYhteensaBig = new Big(viimeisinTavallinen.summa).plus(laskuryppaanSummat.huomautuskorot).plus(laskuryppaanSummat.huomautuskulut)
    const summaYhteensaBigAlv0 = new Big(viimeisinTavallinen.summaAlv0).plus(laskuryppaanSummat.huomautuskorot).plus(laskuryppaanSummat.huomautuskulut)
    const avoinnaBig = summaYhteensaBig.plus(laskuryppaanSummat.hyvitykset).minus(reskontranSumma)

    juurilasku.summaRypas = this.currencyService.muutaBigDecimalRahaksi(summaYhteensaBig)
    juurilasku.summaRypasAlv0 = this.currencyService.muutaBigDecimalRahaksi(summaYhteensaBigAlv0)
    juurilasku.avoinnaRypas = this.currencyService.muutaBigDecimalRahaksi(avoinnaBig)

    collector.addJuurilaskuChange('summaRypas', juurilasku.summaRypas)
    collector.addJuurilaskuChange('summaRypasAlv0', juurilasku.summaRypasAlv0)
    collector.addJuurilaskuChange('avoinnaRypas', juurilasku.avoinnaRypas)
  }

  annaMuotoiltuVirtuaaliviivakoodi(juurilasku: Lasku, kasiteltava: LaskuBase, asetukset: Laskuasetukset): string {
    if (!juurilasku || !kasiteltava || !asetukset) {
      return ''
    }

    const maksettavaa = this.annaMaksettavaa(kasiteltava)
    const viite = this.annaMuotoiltuViitenumero(juurilasku)
    const params: VirtuaaliviivakoodinOsat = {
      iban: asetukset.iban,
      summa: maksettavaa,
      viitenumero: viite,
      erapvm: kasiteltava.erapvml,
    }

    return this.viitenumeroService.luoSuomalainenVirtuaaliviivakoodi(params)
  }

  annaMuotoiltuQrkoodi(juurilasku: Lasku, kasiteltava: LaskuBase, asetukset: Laskuasetukset): string {
    if (!juurilasku || !kasiteltava || !asetukset) {
      return ''
    }

    const maksettavaa = this.annaMaksettavaa(kasiteltava)
    const viite = this.annaMuotoiltuViitenumero(juurilasku)
    const params: LaskunQrKoodinOsat = {
      serviceTag: 'BCD',
      version: '001',
      charset: LaskunQrKoodinCharacterSet.UTF8,
      identificationCode: 'SCT',
      bic: asetukset.bic,
      iban: asetukset.iban,
      maksunSaajaNimi: asetukset.nimi,
      valuutta: kasiteltava.valuutta,
      summa: this.currencyService.muutaBigDecimalRahaksi(maksettavaa),
      purposeCode: null,
      viitenumero: viite,
      erapaiva: kasiteltava.erapvml,
      remittanceInfo: null,
      lisatiedot: null,
    }
    return this.viitenumeroService.luoSuomalainenQrKoodi(params)
  }

  annaViimeisinTavallinenLaskuPaivamaaralle(juurilasku: Lasku, pvm: Date): LaskuBase {
    let viimeinenTavallinen: LaskuBase = juurilasku
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava && (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) && this.dateService.paiviaValissa(pvm, korvaava.pvm.toDate()) > -1) {
        viimeinenTavallinen = korvaava
      }
    }
    return viimeinenTavallinen
  }

  annaViimeisinTavallinenLasku(juurilasku: Lasku): LaskuBase {
    let viimeinenTavallinen: LaskuBase = juurilasku
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava && (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN)) {
        viimeinenTavallinen = korvaava
      }
    }
    return viimeinenTavallinen
  }

  annaKasiteltavaLasku(juurilasku: Lasku, kasiteltavanAvain: string): LaskuBase {
    if (juurilasku.avain === kasiteltavanAvain) {
      return juurilasku
    }
    const kasiteltava = this.annaLiittyvaLaskuAvaimella(juurilasku, kasiteltavanAvain)
    if (kasiteltava) {
      return kasiteltava
    }
    throw new Error('Laskusta ' + juurilasku.avain + ' ei löydy käsiteltävää laskua ' + kasiteltavanAvain)
  }

  annaViimeisinKasiteltavaLasku(juurilasku: Lasku): LaskuBase {
    const viimeinenLiittyva = this.annaViimeinenLiittyvaLasku(juurilasku)
    if (viimeinenLiittyva) {
      return viimeinenLiittyva
    }
    return juurilasku
  }

  annaLaskunKorvaustila(tiedot: LaskuBase): LaskunTila {
    if (tiedot.nrotyyppi === LaskunumeroTyyppi.KORJAUS) {
      return LaskunTila.korvattuLaskulla
    }
    return LaskunTila.korvattuMuistutusLaskulla
  }

  annaLokalisoituMaa(koodi: string, kasiteltava: LaskuBase) {
    if (koodi && kasiteltava && kasiteltava.kieli) {
      return this.countryLocalizationService.getNameTranslateTaxAreaToCountryName(koodi, kasiteltava.kieli)
    }
    return ''
  }

  onkoKasiteltavaMuuKuinLuonnos(juurilasku: Lasku, kasiteltava: LaskuBase): boolean {
    return !this.onkoKasiteltavaLuonnos(juurilasku, kasiteltava)
  }

  onkoKasiteltavaLuonnos(juurilasku: Lasku, kasiteltava: LaskuBase): boolean {
    if (juurilasku.tila !== LaskunTila.luonnos) {
      return false
    }

    const viimeinenLiittyva = this.annaViimeinenLiittyvaLasku(juurilasku)
    const kas = viimeinenLiittyva || juurilasku
    if (kasiteltava.avain === kas.avain) {
      return true
    }

    return false
  }

  voikoTulostaaProforman(juurilasku: Lasku, kasiteltava: LaskuBase): boolean {
    return (
      !!juurilasku &&
      !!kasiteltava &&
      !juurilasku.toistuvaLaskuaihio &&
      juurilasku.avain === kasiteltava.avain &&
      kasiteltava.nrotyyppi !== LaskunumeroTyyppi.HYVITYS &&
      !this.onkoUlkoinenLasku(juurilasku) &&
      this.onkoKasiteltavaLuonnos(juurilasku, kasiteltava)
    )
  }

  onkoKasiteltavaMuuKuinMitatoity(juurilasku: Lasku, kasiteltava: LaskuBase): boolean {
    return !this.onkoKasiteltavaMitatoity(juurilasku, kasiteltava)
  }

  onkoKasiteltavaMitatoity(juurilasku: Lasku, kasiteltava: LaskuBase): boolean {
    if (!juurilasku || !kasiteltava) {
      return false
    }

    if (juurilasku.tila !== LaskunTila.mitatoity) {
      return false
    }

    const viimeinenLiittyva = this.annaViimeinenLiittyvaLasku(juurilasku)
    const kas = viimeinenLiittyva || juurilasku
    if (kasiteltava.avain === kas.avain) {
      return true
    }

    return false
  }

  public annaLaskurivinYhteensaLaskunTuotteelle(tuote: LaskunTuote): number {
    if (!tuote) {
      return null
    }

    if (tuote.hinnanTyyppi === 'yhteensa' && this.currencyService.onkoNumero(tuote.yhteensa)) {
      return this.currencyService.muutaBigDecimalRahaksi(new Big(tuote.yhteensa))
    } else {
      const rivinSummat = this.annaRivinSummatLaskettunaKappalehinnasta(tuote)
      return this.currencyService.muutaBigDecimalRahaksi(rivinSummat.yhteensaAlvnKanssa)
    }
  }

  public annaLaskurivinYhteensaIlmanAlvLaskunTuotteelle(tuote: LaskunTuote): number {
    if (!tuote) {
      return null
    }
    const summat = this.annaYhdenLaskunTuotteenSummat(tuote)
    return this.currencyService.muutaBigDecimalRahaksi(summat.yhteensaIlmanAlv)
  }

  // private annaUseanLaskunSummat(kasiteltavat: LaskuBase[]): LaskunSummat {
  //   const erittely: { [key: string]: AlvErittelynOsa } = {}
  //   const summat: LaskunSummat = {
  //     alvErittely: [],
  //     yhteensaAlv: new Big('0'),
  //     yhteensaIlmanAlv: new Big('0'),
  //     yhteensaKaikki: new Big('0'),
  //     alennustaYhteensa: new Big('0'),
  //     alvErittelyMap: erittely
  //   }

  //   for (const kasiteltava of kasiteltavat) {
  //     const kasiteltavanSummat = this.annaLaskunSummat(kasiteltava)
  //     this.lisaaAlvErittelynSummatToiseen(summat, erittely, kasiteltavanSummat.alvErittelyMap)
  //     summat.yhteensaAlv = summat.yhteensaAlv.plus(kasiteltavanSummat.yhteensaAlv)
  //     summat.yhteensaIlmanAlv = summat.yhteensaIlmanAlv.plus(kasiteltavanSummat.yhteensaIlmanAlv)
  //     summat.yhteensaKaikki = summat.yhteensaKaikki.plus(kasiteltavanSummat.yhteensaKaikki)
  //     summat.alennustaYhteensa = summat.yhteensaAlv.plus(kasiteltavanSummat.alennustaYhteensa)
  //   }

  //   return summat
  // }

  // private lisaaAlvErittelynSummatToiseen(summat: LaskunSummat, erittely: { [key: string]: AlvErittelynOsa }, tastaLisataan: { [key: string]: AlvErittelynOsa }) {
  //   for (const key of Object.keys(tastaLisataan)) {
  //     const erittelynOsa: AlvErittelynOsa = tastaLisataan[key]
  //     if (erittelynOsa) {
  //       let osa: AlvErittelynOsa = erittely[key]
  //       if (!osa) {
  //         osa = {
  //           alvitonSumma: new Big('0'),
  //           alvinSumma: new Big('0'),
  //           alvKanta: erittelynOsa.alvKanta
  //         }
  //         erittely[key] = osa
  //         summat.alvErittely.push(osa)
  //       }
  //       osa.alvitonSumma = osa.alvitonSumma.plus(erittelynOsa.alvitonSumma)
  //       osa.alvinSumma =  osa.alvinSumma.plus(erittelynOsa.alvinSumma)
  //     }
  //   }
  // }

  private _lisaaLaskurivinAlvSummat(tuote: LaskunTuote, summat: LaskunSummat, erittely: { [key: string]: AlvErittelynOsa }) {
    if (tuote.hinnanTyyppi === 'yhteensa' && this.currencyService.onkoNumero(tuote.yhteensa)) {
      const alvKerroin = this.annaAlvKerroin(tuote)
      const aleKerroin = this.annaAleKerroin(tuote)
      const alennusta = !aleKerroin.eq('1')
      const riviYhteensa = new Big(tuote.yhteensa).round(2, Big.roundHalfUp)
      const yhteensaIlmanAlv = riviYhteensa.div(alvKerroin) // .round(2, Big.roundHalfUp)
      const alvinOsuus = riviYhteensa.minus(yhteensaIlmanAlv) // .round(2, Big.roundHalfUp)
      const alennusIlmanAlv = alennusta ? this._jaaAlennuksella(yhteensaIlmanAlv, aleKerroin).minus(yhteensaIlmanAlv) : new Big('0')
      const alennusAlvinOsuus = alennusta ? this._jaaAlennuksella(alvinOsuus, aleKerroin).minus(alvinOsuus) : new Big('0')

      this.lisaaAlvErittelynSummat(tuote, erittely, summat, yhteensaIlmanAlv, alvinOsuus, alennusIlmanAlv, alennusAlvinOsuus)
    } else {
      const rivinSummat = this.annaRivinSummatLaskettunaKappalehinnasta(tuote)
      // Yhteensä ilman alvia
      const yhteensaIlmanAlv = rivinSummat.yhteensaAlvnKanssa.minus(rivinSummat.alvta) // .round(2, Big.roundHalfUp)
      const yhteensaIlmanAlvAle = rivinSummat.aleAlvnKanssa.minus(rivinSummat.aleAlvta) // .round(2, Big.roundHalfUp)
      this.lisaaAlvErittelynSummat(tuote, erittely, summat, yhteensaIlmanAlv, rivinSummat.alvta, yhteensaIlmanAlvAle, rivinSummat.aleAlvta)
    }
  }

  private _jaaAlennuksella(summa: Big, aleKerroin: Big) {
    if (aleKerroin.eq('0')) {
      // Avoid divide by zero
      return new Big('0')
    }
    return summa.div(aleKerroin)
  }

  annaYhdenLaskunTuotteenSummat(tuote: LaskunTuote): LaskunSummat {
    const erittely: { [key: string]: AlvErittelynOsa } = {}
    const summat: LaskunSummat = {
      alvErittely: [],
      yhteensaAlv: new Big('0'),
      yhteensaIlmanAlv: new Big('0'),
      yhteensaKaikki: new Big('0'),
      alennustaYhteensaIlmanAlv: new Big('0'),
      alennustaYhteensaAlv: new Big('0'),
      alvErittelyMap: erittely,
    }

    this._lisaaLaskurivinAlvSummat(tuote, summat, erittely)
    summat.yhteensaKaikki = summat.yhteensaIlmanAlv.plus(summat.yhteensaAlv) // .round(2, Big.roundHalfUp)

    summat.yhteensaAlv = summat.yhteensaAlv.round(2, Big.roundHalfUp)
    summat.yhteensaIlmanAlv = summat.yhteensaIlmanAlv.round(2, Big.roundHalfUp)
    summat.yhteensaKaikki = summat.yhteensaKaikki.round(2, Big.roundHalfUp)
    summat.alennustaYhteensaIlmanAlv = summat.alennustaYhteensaIlmanAlv.round(2, Big.roundHalfUp)
    summat.alennustaYhteensaAlv = summat.alennustaYhteensaAlv.round(2, Big.roundHalfUp)

    return summat
  }

  annaMaksettavaa(kasiteltava: LaskuBase): Big {
    const summat = this.annaLaskunSummat(kasiteltava)
    if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      return summat.yhteensaKaikki.plus(kasiteltava.summaHyvityksetLaskunPvm).minus(kasiteltava.summaReskontraLaskunPvm)
    }
    return summat.yhteensaKaikki
  }

  annaLaskunSummat(kasiteltava: LaskuBase): LaskunSummat {
    const erittely: { [key: string]: AlvErittelynOsa } = {}
    const summat: LaskunSummat = {
      alvErittely: [],
      yhteensaAlv: new Big('0'),
      yhteensaIlmanAlv: new Big('0'),
      yhteensaKaikki: new Big('0'),
      alennustaYhteensaIlmanAlv: new Big('0'),
      alennustaYhteensaAlv: new Big('0'),
      alvErittelyMap: erittely,
    }

    if (kasiteltava && kasiteltava.tuotteet) {
      for (const tuote of kasiteltava.tuotteet) {
        this._lisaaLaskurivinAlvSummat(tuote, summat, erittely)
      }
      summat.yhteensaKaikki = summat.yhteensaIlmanAlv.plus(summat.yhteensaAlv) // .round(2, Big.roundHalfUp)
    }

    summat.yhteensaAlv = summat.yhteensaAlv.round(2, Big.roundHalfUp)
    summat.yhteensaIlmanAlv = summat.yhteensaIlmanAlv.round(2, Big.roundHalfUp)
    summat.alennustaYhteensaAlv = summat.alennustaYhteensaAlv.round(2, Big.roundHalfUp)
    summat.alennustaYhteensaIlmanAlv = summat.alennustaYhteensaIlmanAlv.round(2, Big.roundHalfUp)
    summat.yhteensaKaikki = summat.yhteensaKaikki.round(2, Big.roundHalfUp)

    return summat
  }

  konvertoiLaskunSummat(lasku: Lasku, kasiteltava: LaskuBase, summat: LaskunSummat, toinenKonversio?: ValuuttakonversionTiedotKirjauksessa): LaskunSummat {
    if (!kasiteltava.valuuttakonversio?.kurssi?.rate && !toinenKonversio?.kurssi?.rate) {
      throw new Error('Laskun valuuttakonversiota ei ole määritetty, joten summia ei voida konvertoida. Lasku avain: ' + lasku.avain + ', käsiteltävä: ' + kasiteltava.avain)
    }

    const vaihtokurssi = toinenKonversio?.kurssi?.rate || kasiteltava.valuuttakonversio.kurssi.rate

    // Apufunktio joka muuntaa Big-arvon euroon käyttäen vaihtokurssia
    const konvertoiSumma = (bigArvo: Big): number => {
      const summaAlkuperaisessaValuutassa = this.currencyService.muutaBigDecimalRahaksi(bigArvo)
      return summaAlkuperaisessaValuutassa * vaihtokurssi
    }

    // Konvertoidaan kaikki ylätason summat
    const yhteensaIlmanAlvConverted = konvertoiSumma(summat.yhteensaIlmanAlv)
    const yhteensaAlvConverted = konvertoiSumma(summat.yhteensaAlv)
    const yhteensaKaikkiConverted = konvertoiSumma(summat.yhteensaKaikki)
    const alennustaYhteensaIlmanAlvConverted = konvertoiSumma(summat.alennustaYhteensaIlmanAlv)
    const alennustaYhteensaAlvConverted = konvertoiSumma(summat.alennustaYhteensaAlv)

    // Konvertoidaan ALV-erittelyn jokainen osa
    const alvErittelyConverted: AlvErittelynOsa[] = []
    for (const alvOsa of summat.alvErittely) {
      alvErittelyConverted.push({
        alennustaIlmanAlv: new Big(konvertoiSumma(alvOsa.alennustaIlmanAlv)),
        alennustaAlv: new Big(konvertoiSumma(alvOsa.alennustaAlv)),
        alvitonSumma: new Big(konvertoiSumma(alvOsa.alvitonSumma)),
        alvinSumma: new Big(konvertoiSumma(alvOsa.alvinSumma)),
        alvKanta: alvOsa.alvKanta,
        projekti: alvOsa.projekti,
      })
    }

    // Luodaan alvErittelyMap konvertoiduista arvoista
    const alvErittelyMapConverted: { [key: string]: AlvErittelynOsa } = {}
    for (const alvOsa of alvErittelyConverted) {
      alvErittelyMapConverted[alvOsa.alvKanta.tunniste] = alvOsa
    }

    return {
      yhteensaIlmanAlv: new Big(yhteensaIlmanAlvConverted),
      yhteensaAlv: new Big(yhteensaAlvConverted),
      yhteensaKaikki: new Big(yhteensaKaikkiConverted),
      alennustaYhteensaIlmanAlv: new Big(alennustaYhteensaIlmanAlvConverted),
      alennustaYhteensaAlv: new Big(alennustaYhteensaAlvConverted),
      alvErittely: alvErittelyConverted,
      alvErittelyMap: alvErittelyMapConverted,
    }
  }

  public annaAlvKerroin(tuote: LaskunTuote): Big {
    const alv = ((tuote.alv || {}) as LaskunAlv).prosentti || 0
    return new Big(alv ? 1 + alv / 100 : 1).round(3, Big.roundHalfUp)
  }

  public annaAleKerroin(tuote: LaskunTuote) {
    const aleKerroin = new Big(1 - (tuote.ale ? tuote.ale / 100 : 0)).round(4, Big.roundHalfUp)
    if (aleKerroin.lt('0')) {
      return new Big('1')
    }
    return aleKerroin
  }

  public annaRivinSummatLaskettunaKappalehinnasta(tuote: LaskunTuote): { alvta: Big; yhteensaAlvnKanssa: Big; aleAlvta: Big; aleAlvnKanssa: Big } {
    const hinta = new Big(tuote.hinta || 0).round(2, Big.roundHalfUp)
    const maara = new Big(tuote.maara || 0).round(4, Big.roundHalfUp)

    // Alv ja ale
    const alvKerroin = this.annaAlvKerroin(tuote)
    const aleKerroin = this.annaAleKerroin(tuote)
    const alennusta = !aleKerroin.eq('1')

    // Laske ensin veroton alennettu hinta
    const alennettuYksikkohinta = hinta.times(aleKerroin) // .round(2, Big.roundHalfUp)

    // console.log('!', tuote.ale, alennettuYksikkohinta.toNumber(),)
    // Sitten alennettu hinta alvin kanssa
    const yksikkoHintaAlvnKanssa = alennettuYksikkohinta.times(alvKerroin) // .round(2, Big.roundHalfUp)

    // Alvia per tuote
    const alvPerTuote = yksikkoHintaAlvnKanssa.minus(alennettuYksikkohinta) // .round(2, Big.roundHalfUp)

    // Yhteensä rivillä alvia
    const alvinOsuus = alvPerTuote.times(maara) // .round(2, Big.roundHalfUp)

    // Rivi yhteensä
    const riviYhteensa = yksikkoHintaAlvnKanssa.times(maara) // .round(2, Big.roundHalfUp)

    const tuotteetIlmanAlennustaAlv0 = hinta.times(maara)
    const tuotteetIlmanAlennusta = hinta.times(alvKerroin).times(maara)

    const alennuksenMaaraAlv0 = alennusta ? tuotteetIlmanAlennustaAlv0.minus(riviYhteensa.minus(alvinOsuus)) : new Big('0')
    const alennuksenMaara = alennusta ? tuotteetIlmanAlennusta.minus(riviYhteensa) : new Big('0')
    const aleAlvnMaara = alennuksenMaara.minus(alennuksenMaaraAlv0)

    return {
      alvta: alvinOsuus,
      yhteensaAlvnKanssa: riviYhteensa,
      aleAlvta: aleAlvnMaara,
      aleAlvnKanssa: alennuksenMaara,
    }
  }

  private lisaaAlvErittelynSummat(
    tuote: LaskunTuote,
    erittely: { [key: string]: AlvErittelynOsa },
    summat: LaskunSummat,
    yhteensaIlmanAlv: Big,
    alvinOsuus: Big,
    alennusIlmanAlv: Big,
    alennusAlvinOsuus: Big,
  ) {
    if (tuote.alv) {
      let osa: AlvErittelynOsa = erittely[tuote.alv.tunniste]
      if (!osa) {
        osa = {
          alvitonSumma: new Big('0'),
          alvinSumma: new Big('0'),
          alvKanta: tuote.alv,
          alennustaIlmanAlv: new Big('0'),
          alennustaAlv: new Big('0'),
        }
        erittely[tuote.alv.tunniste] = osa
        summat.alvErittely.push(osa)
      }
      osa.alvitonSumma = osa.alvitonSumma.plus(yhteensaIlmanAlv)
      osa.alvinSumma = osa.alvinSumma.plus(alvinOsuus)
      osa.alennustaIlmanAlv = osa.alennustaIlmanAlv.plus(alennusIlmanAlv)
      osa.alennustaAlv = osa.alennustaAlv.plus(alennusAlvinOsuus)
      summat.yhteensaAlv = summat.yhteensaAlv.plus(alvinOsuus)
      summat.alennustaYhteensaAlv = summat.alennustaYhteensaAlv.plus(alennusAlvinOsuus)
    }
    summat.yhteensaIlmanAlv = summat.yhteensaIlmanAlv.plus(yhteensaIlmanAlv)
    summat.alennustaYhteensaIlmanAlv = summat.alennustaYhteensaIlmanAlv.plus(alennusIlmanAlv)
  }

  public annaAlvHuomautus(kasiteltava: LaskuBase): string | null {
    if (!kasiteltava) {
      return null
    }
    if (
      kasiteltava.tyyppi === LaskunTyypit.RAKENNUSALA.avain ||
      kasiteltava.tyyppi === LaskunTyypit.EU_TAVARA.avain ||
      kasiteltava.tyyppi === LaskunTyypit.EU_PALVELU.avain ||
      kasiteltava.tyyppi === LaskunTyypit.MUU_MAAILMA.avain
    ) {
      return this.annaLokalisoituMerkkijono('alv-huomautus.' + kasiteltava.tyyppi, kasiteltava)
    }
    return null
  }

  public annaViimeisinEiLuonnosLasku(juurilasku: Lasku): LaskuBase {
    let kasiteltava: LaskuBase = juurilasku
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    if (liittyvatLaskut.length) {
      const indeksi = juurilasku.tila === LaskunTila.luonnos ? liittyvatLaskut.length - 2 : liittyvatLaskut.length - 1
      if (indeksi > -1) {
        kasiteltava = liittyvatLaskut[indeksi]
      }
    }
    return kasiteltava
  }

  public annaViimeisinEiLuonnosLaskuTaiNullJosVainLuonnos(juurilasku: Lasku): LaskuBase {
    const eiLiittyviaLaskuja = !this.annaLiittyvatLaskut(juurilasku).length
    if (juurilasku.tila === LaskunTila.luonnos && eiLiittyviaLaskuja) {
      return null
    }
    return this.annaViimeisinEiLuonnosLasku(juurilasku)
  }

  public annaViimeisinMuistutuslasku(juurilasku: Lasku): LaskuBase | null {
    let viimeisinMuistutus = null
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        viimeisinMuistutus = korvaava
      }
    }
    return viimeisinMuistutus
  }

  public annaViimeisinMuokattuLasku(juurilasku: Lasku): LaskuBase {
    let viimeisinMuokattu: LaskuBase = juurilasku
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN || korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS) {
        viimeisinMuokattu = korvaava
      }
    }
    return viimeisinMuokattu
  }

  // public annaLaskunTila(reskontraSumma: Big, laskunSumma: Big, juurilasku: Lasku, kasiteltava: LaskuBase): LaskunTila {
  //   const avoinna = laskunSumma.minus(reskontraSumma)
  //   if (avoinna.round(2, Big.roundHalfUp).eq('0')) {
  //     return LaskunTila.maksettu
  //   } else if (avoinna.round(2, Big.roundHalfUp).lt('0')) {
  //     return LaskunTila.maksettuLiikaa
  //   } else if (this.onkoLaskunErapaivaMennyt(kasiteltava)) {
  //     return LaskunTila.eraantynyt
  //   }
  //   return LaskunTila.avoin
  // }

  onkoLaskunErapaivaMennytPaivalle(kasiteltava: LaskuBase, pvm: Date): boolean {
    return this.dateService.paiviaValissaTimestamp(kasiteltava.erapvm, this.timestampProvider.dateToTimestamp(pvm)) < 0
  }

  onkoLaskunErapaivaMennyt(kasiteltava: LaskuBase): boolean {
    return this.dateService.paiviaValissaTimestamp(kasiteltava.erapvm, this.timestampProvider.now()) < 0
  }

  public annaLokalisoituMuistutus(juurilasku: Lasku, kasiteltava: LaskuBase): string {
    const viimeisinMuokattu = this.annaViimeisinMuokattuLasku(juurilasku)

    const erapaiva = this.formatoiPvm(viimeisinMuokattu.erapvml, viimeisinMuokattu.erapvm, kasiteltava)
    const laskunumero = this.annaMuotoiltuLaskunumero(juurilasku, kasiteltava)

    return this.annaLokalisoituMerkkijono('pdf-perinteinen.muistutus', kasiteltava, { laskunumero: laskunumero, erapaiva: erapaiva })
  }

  public annaLokalisoituLaskunOtsikko(juurilasku: Lasku, kasiteltava: LaskuBase, maksettavaaYhteensa: Big): string {
    const laskunOtsikonAvain = this.annaLaskunOtsikonAvain(juurilasku, kasiteltava, maksettavaaYhteensa)
    return this.annaLokalisoituMerkkijono('pdf-perinteinen.' + laskunOtsikonAvain, kasiteltava)
  }

  public annaLaskunOtsikonAvain(juurilasku: Lasku, kasiteltava: LaskuBase, maksettavaaYhteensa: Big): LaskunOtsikonAvain {
    if (this.onkoKasiteltavaMitatoity(juurilasku, kasiteltava)) {
      return 'mitatoity'
    }
    if (this.onkoKasiteltavaLuonnos(juurilasku, kasiteltava)) {
      return 'luonnos'
    }
    if (maksettavaaYhteensa && maksettavaaYhteensa.lt('0')) {
      return 'hyvityslasku'
    }
    if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      return 'muistutuslasku'
    }
    if (juurilasku.avain === kasiteltava.avain && juurilasku.ennakkolasku) {
      return 'ennakkolasku'
    }
    return 'lasku'
  }

  public annaLokalisoituValuutanNimi(kasiteltava: LaskuBase): string {
    if (kasiteltava.valuutta === 'EUR') {
      return this.annaLokalisoituMerkkijono('pdf-perinteinen.euro', kasiteltava)
    }
    return kasiteltava.valuutta
  }

  /**
   * Palauttaa laskun jonka kasiteltava on korvannut. (edellinen lasku)
   * @param juurilasku
   * @param kasiteltava
   */
  public annaKorvattuLasku(juurilasku: Lasku, kasiteltava: LaskuBase): LaskuBase | null {
    if (!juurilasku || !kasiteltava) {
      return null
    }
    if (juurilasku.avain === kasiteltava.avain) {
      return null
    }
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    if (liittyvatLaskut.length) {
      let korvattu: LaskuBase = juurilasku
      for (const korvaava of liittyvatLaskut) {
        if (korvaava.avain === kasiteltava.avain) {
          return korvattu
        }
        korvattu = korvaava
      }
    }
    return null
  }

  /**
   * Palauttaa laskun, joka on korvannut tämän (ei viimeisin, vaan seuraava)
   * @param juurilasku
   * @param kasiteltava
   */
  public annaKorvaavaLasku(juurilasku: Lasku, kasiteltava: LaskuBase): LaskuBase | null {
    if (!juurilasku || !kasiteltava) {
      return null
    }
    let palautaSeuraava = juurilasku.avain === kasiteltava.avain
    for (const korvaava of this.annaLiittyvatLaskut(juurilasku)) {
      if (palautaSeuraava) {
        return korvaava
      }
      if (korvaava.avain === kasiteltava.avain) {
        palautaSeuraava = true
      }
    }
    return null
  }

  public annaLisatiedot(juurilasku: Lasku, kasiteltava: LaskuBase): string {
    if (!juurilasku || !kasiteltava) {
      return null
    }

    if (this.onkoUlkoinenLasku(kasiteltava)) {
      return kasiteltava.lisatiedot
    }

    const onkoKorvaava = juurilasku.avain !== kasiteltava.avain

    let lisatiedot = ''
    if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.HYVITYS && onkoKorvaava) {
      // Lisätään hyvityslaskulle lisätietoteksti ainoastaan, jos hyvitetään jo olemassa olevaa laskua (eli erillishyvitykselle ei lisätietotekstiä).
      const muotoiltuKorvaava = this.annaMuotoiltuLaskunumero(juurilasku, juurilasku)
      lisatiedot = this.annaLokalisoituMerkkijono('pdf-perinteinen.hyvittaa-laskun', kasiteltava, { korvatunLaskunNumero: muotoiltuKorvaava })
    } else if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
      const maara = this.annaMuistutuslaskunNumero(juurilasku, kasiteltava)
      if (maara > 6) {
        lisatiedot = this.annaLokalisoituMerkkijono('pdf-perinteinen.monesko-muistutuslasku', kasiteltava, { muistutuslaskujenMaara: maara })
      } else {
        lisatiedot = this.annaLokalisoituMerkkijono('pdf-perinteinen.monesko-muistutuslasku-' + maara, kasiteltava, { muistutuslaskujenMaara: maara })
      }
    } else if (kasiteltava.nrotyyppi === LaskunumeroTyyppi.KORJAUS) {
      const muotoiltuKorvaava = this.annaMuotoiltuLaskunumero(juurilasku, juurilasku)
      lisatiedot = this.annaLokalisoituMerkkijono('pdf-perinteinen.korvaa-laskun', kasiteltava, { korvatunLaskunNumero: muotoiltuKorvaava })
    }

    lisatiedot = lisatiedot.trim()

    if (kasiteltava.lisatiedot) {
      if (lisatiedot) {
        lisatiedot += ' '
      }
      lisatiedot += kasiteltava.lisatiedot.trim()
    }

    if (lisatiedot === '') {
      return null
    }

    return lisatiedot
  }

  annaHyvityslaskunNumero(juurilasku: Lasku, kasiteltava: LaskuBase): number {
    if (kasiteltava.nrotyyppi !== LaskunumeroTyyppi.HYVITYS) {
      throw new Error('kasiteltava EI ole hyvitys, ' + kasiteltava.nrotyyppi)
    }
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    if (!liittyvatLaskut.length) {
      return 1
    }
    let maara = 1
    for (const korvaava of liittyvatLaskut) {
      if (korvaava.avain === kasiteltava.avain) {
        break
      }
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.HYVITYS) {
        maara += 1
      }
    }
    return maara
  }

  annaMuistutuslaskunNumero(juurilasku: Lasku, kasiteltava: LaskuBase): number {
    // HUOM! Jos ehtoja muutetaan, katso myös this.annaLaskuryppaanMuistutuslaskut

    if (kasiteltava.nrotyyppi !== LaskunumeroTyyppi.MUISTUTUS) {
      throw new Error('kasiteltava EI ole muistutus, ' + kasiteltava.nrotyyppi)
    }
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    if (!liittyvatLaskut.length) {
      return 0
    }
    let maara = 0

    for (const korvaava of liittyvatLaskut) {
      if (korvaava.avain === kasiteltava.avain) {
        // console.log('Löytyi', korvaava.avain)
        maara++
        break
      }
      if (korvaava.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS) {
        // console.log('MUISTUTUS', korvaava.avain, korvaava.date, korvaava.nrotyyppi)
        maara++
      } else if (korvaava.nrotyyppi === LaskunumeroTyyppi.KORJAUS || korvaava.nrotyyppi === LaskunumeroTyyppi.TAVALLINEN) {
        // KATSO MYÖS annaMuistutukset metodi!
        maara = 0
      }
    }
    // console.log('maara', maara)
    return maara
  }

  onkoUlkoinenLasku(lasku: LaskuBase): boolean {
    return !!(lasku.ulkoinenLaskunro || lasku.source)
  }

  annaUlkoisenLaskunPalvelunNimi(source: InvoiceSource, kieli: TuettuKieli): string {
    if (source === InvoiceSource.MINDUU) {
      return this.translationService.lokalisoi('ulkoiset-laskut.minduu', kieli)
    }
    return this.translationService.lokalisoi('ulkoiset-laskut.tuntematon', kieli)
  }

  muotoileYtunnusLaskunTyypinPerusteella(kasiteltava: LaskuBase, ytunnus: string) {
    if (kasiteltava?.tyyppi === LaskunTyypit.TAVALLINEN.avain || kasiteltava?.tyyppi === LaskunTyypit.RAKENNUSALA.avain) {
      return this._codeCheckService.annaYTunnus(ytunnus)
    }
    return this._codeCheckService.annaVatTunnus(ytunnus)
  }

  annaLiittyvatLaskut(juurilasku: Pick<Lasku, 'liittyvatLaskut'>): LaskuBase[] {
    if (juurilasku.liittyvatLaskut) {
      // HUOM!! Osa logiikasta odottaa, että nämä laskut ovat aina järjestyksessä.
      return Object.values(juurilasku.liittyvatLaskut).sort((a, b) => {
        if (a.p && b.p && a.p !== b.p) {
          return a.p - b.p
        }
        if (a.luotu?.toMillis && b.luotu?.toMillis) {
          return a.luotu.toMillis() - b.luotu.toMillis()
        }
        return 0
      })
    }
    return []
  }

  annaLiittyvaLaskuAvaimella(juurilasku: Lasku, etsittavaAvain: string): LaskuBase | null {
    if (juurilasku.liittyvatLaskut) {
      return juurilasku.liittyvatLaskut[etsittavaAvain] ?? null
    }
    return null
  }

  annaViimeinenLiittyvaLasku(juurilasku: Lasku): LaskuBase | null {
    const liittyvatLaskut = this.annaLiittyvatLaskut(juurilasku)
    if (!liittyvatLaskut.length) {
      return null
    }
    return liittyvatLaskut[liittyvatLaskut.length - 1]
  }

  lisaaLiittyviinLaskuihin(juurilasku: Lasku, lisattava: LaskuBase, collector: LaskuChangeCollectorInterface) {
    if (!juurilasku) {
      throw new Error('Ei juurilaskua')
    }
    if (!juurilasku.liittyvatLaskut) {
      juurilasku.liittyvatLaskut = {}
    }
    juurilasku.liittyvatLaskut[lisattava.avain] = lisattava
    collector.setKasiteltavaAsTheChange(lisattava)
  }

  lisaaLiittyviinLaskuihinIlmanMuutostenKeraamista(juurilasku: Lasku, lisattava: LaskuBase) {
    if (!juurilasku) {
      throw new Error('Ei juurilaskua')
    }
    if (!juurilasku.liittyvatLaskut) {
      juurilasku.liittyvatLaskut = {}
    }
    juurilasku.liittyvatLaskut[lisattava.avain] = lisattava
  }

  /** Delete only done in memory to avoid including the heavy .haku field where it's not needed */
  poistaKaikkiLaskunHakuKentatMuistista(lasku: Lasku) {
    delete lasku['haku']
    if (lasku.liittyvatLaskut) {
      for (const liittyvatLasku of Object.values(lasku.liittyvatLaskut)) {
        delete liittyvatLasku['haku']
      }
    }
  }
}
